import './PromptChainList.scss';

import React, { ChangeEvent, FC, SyntheticEvent, useContext, useEffect } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import TableSearch from 'components/TableSearch/TableSearch';
import { NavHeadings } from 'constants/Constants';
import { useDataSet } from 'customHooks/useDataSet';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import DataSetOperators, { SearchType, SortDirection } from 'interfaces/DataSetOperators';
import DataWrapper from 'interfaces/DataWrapper';
import { Entities } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';
import { Project } from 'models/Project';
import { PromptFlow } from 'models/PromptFlow';
import { Button, Col, Row } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';

import PromptTableActionButtonGroup from '../PromptTableActionButtonGroup/PromptTableActionButtonGroup';


// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface PromptChainListProps { }

const PromptChainList: FC<PromptChainListProps> = () => {
  const { configRepo, selectedProjectId, setIsProjectSelectable, notify, sendLog, projects } = useContext(AppContext);
  const initialParams: DataSetOperators = {
    SortBy: 'Name',
    SortDirection: SortDirection.Asc,
    Top: 10
  };
  const { dataSet, setOperators, operators } = useDataSet<PromptFlow>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.PromptFlows,
    selectedProjectId,
    initialParams,
  );
  const dataWrapper: DataWrapper<PromptFlow> =
    dataSet.responseData as DataWrapper<PromptFlow>;
  const promptFlows: PromptFlow[] = dataWrapper?.data;
  const [resetPaginationToggle, setResetPaginationToggle] = React.useState(false);
  const [filterText, setFilterText] = React.useState('');

  useEffect(() => {
    setIsProjectSelectable(true);
  }, []);

  useEffect(() => {
    if (operators.ProjectId !== selectedProjectId) {
      setResetPaginationToggle(true);
      const ops = { ...operators };
      ops.Skip = 0;
      ops.ProjectId = selectedProjectId;
      ops.SortBy = 'Name';
      ops.SortDirection = SortDirection.Asc;
      setOperators(ops);
    }
  }, [selectedProjectId]);

  const onTokenExpired = useTokenExpiredHandler();
  const { deleteEntity } = useModifyEntityService<PromptFlow>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.PromptFlows,
    sendLog,
    onTokenExpired,
  );

  const onDelete = async (id: number): Promise<void> => {
    if (await deleteEntity(id)) {
      notify('Prompt flow has been deleted successfully.', NotificationType.Success);
      sendLog(`deleted successfully prompt flow: ${id}`, LogLevel.info);
      const ops = { ...operators };
      setOperators(ops);
    } else {
      notify('Prompt flow has not been deleted successfully.', NotificationType.Error);
      sendLog(`failed to delete prompt flow: ${id}`, LogLevel.error);
    }
  };

  const columns = [
    {
      name: 'Actions',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (row: PromptFlow) => (
        <PromptTableActionButtonGroup
          entityContainerUrl='prompt-chains'
          entityId={row.id!}
          entitySlug={row.slug}
          key={row.id}
          isViewOnly={row.projectId !== selectedProjectId}
          onDelete={onDelete}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: false,
      button: true,
      className: 'padd-left',
      width: '150px',
    },
    {
      name: 'Name',
      selector: (row: PromptFlow) => row.name,
      type: 'name',
      sortable: true,
      // width: '120px',
    },
    {
      name: 'Short Description',
      selector: (row: PromptFlow) => row.metaDescription,
      type: 'metaDescription',
      sortable: true,
      // width: '200px',
    },
    {
      name: 'Target Action',
      selector: (row: PromptFlow) => row.targetAction.split(/(?=[A-Z])/).join(' '),
      type: 'targetAction',
      sortable: true,
      // width: '300px',
    },
    {
      name: 'Visibility',
      selector: (row: PromptFlow) => row.visibility,
      type: 'visibility',
      sortable: true,
      // width: '300px',
    }
  ];

  const handleSort = (column: any, sortDirection: any) => {
    const ops = { ...operators };
    ops.SortBy = column.type;
    ops.SortDirection = sortDirection === 'asc' ? SortDirection.Asc : SortDirection.Desc;
    setOperators(ops);
  };

  const handlePerRowsChange = (page: number) => {
    const ops = { ...operators };
    ops.Top = page;
    setOperators(ops);
  };

  const handlePageChange = (page: number) => {
    const ops = { ...operators };
    if (page === 1) {
      ops.Skip = 0;
    } else {
      ops.Skip = ops.Top! * (page - 1);
    }
    setOperators(ops);
  };

  // see: https://react-data-table-component.netlify.app/?path=/docs/examples-filtering--filtering
  const onSearchTextChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
  };

  const applySearch = (terms: string | undefined) => {
    const ops = { ...operators };
    if (terms) {
      ops.SearchProperty = 'Name|MetaTitle|MetaDescription|MetaKeywords';
      ops.SearchType = SearchType.Contains;
      ops.SearchValue = terms;
    } else {
      ops.SearchProperty = undefined;
      ops.SearchType = undefined;
      ops.SearchValue = undefined;
    }
    // this is necessary
    // test: switch project A > search 'T' > switch project B > clear search
    // => results are project A unfiltered without this
    ops.ProjectId = selectedProjectId;
    setOperators(ops);
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText('');
        applySearch(undefined);
      }
    };

    const onFilter = (e: SyntheticEvent) => {
      e.preventDefault();
      applySearch(filterText);
    };

    return (
      <div className="action-panel">
        <Row>
          <Col md={6} xs={6} className="g-0">
            <TableSearch
              onFilter={onFilter}
              onClear={handleClear}
              filterPropertyName="Name or Description"
              searchText={filterText}
              onSearchTextChanged={onSearchTextChanged}
            />
          </Col>
          <Col md={6} xs={6} className="text-end">
            <Button variant="primary" href="/prompt-chains/edit/new">
              <i className='bi bi-plus-lg pr-1' />
              New Prompt Chain
            </Button>
          </Col>
        </Row>
      </div>
    );
  }, [filterText, resetPaginationToggle, selectedProjectId]);

  const heading: NavHeadingProps = {
    title: `${projects.find((p: Project) => p.id === selectedProjectId)?.name} Prompt Chains`,
    ...NavHeadings.PromptChains,
  };

  return (
    <div className=''>
      <NavHeading {...heading} />
      <div className="data-collection">
        <DataTable
          columns={columns as TableColumn<PromptFlow>[]}
          defaultSortFieldId="2"
          defaultSortAsc={false}
          className="tbl-prompt-flows"
          data={promptFlows}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={dataWrapper?.metaData?.totalRecordCount ?? 0}
          paginationResetDefaultPage={resetPaginationToggle}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          persistTableHead
        // fixedHeader
        // fixedHeaderScrollHeight="500px"
        // dense
        />
      </div>
    </div>
  );
}

export default PromptChainList;
